import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteDoctorDocument,
  downloadDoctorDocument,
  getDoctorProfile,
  listDoctorDocuments,
  listSpecializations,
  submitDoctorForReview,
  updateDoctorProfile,
  uploadDoctorDocument,
  type SubmitDoctorVerificationBody,
} from "@/api/userApi";
import { PageHeader } from "@/components/common/PageHeader";
import type {
  DoctorDetail,
  DoctorDocumentDto,
  DoctorDocumentType,
  DoctorProfileStatus,
  Specialization,
} from "@/types/domain";

// ── Qualification presets ────────────────────────────────────────────────

const QUALIFICATION_OPTIONS = [
  "MBBS",
  "BDS",
  "BAMS",
  "BHMS",
  "BUMS",
  "MD",
  "MS",
  "DNB",
  "DM",
  "MCh",
  "MDS",
  "MPH",
  "MBBS, MD",
  "MBBS, MS",
  "BDS, MDS",
  "Other",
] as const;

// ── Form state types ─────────────────────────────────────────────────────

interface BasicFormState {
  fullName: string;
  qualification: string;
  qualificationCustom: string;
  experienceYears: string;
  consultationFee: string;
  bio: string;
  specializationNames: string[];
}

interface VerificationFormState {
  registrationNumber: string;
  registrationCouncil: string;
  registrationYear: string;
}

interface OtherDocUpload {
  file: File | null;
  notes: string;
}

// ── Tab panel helper ─────────────────────────────────────────────────────

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export default function DoctorOnboardingPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["doctor-profile"],
    queryFn: getDoctorProfile,
  });

  const { data: specList } = useQuery({
    queryKey: ["specializations"],
    queryFn: listSpecializations,
  });

  const { data: existingDocs, refetch: refetchDocs } = useQuery({
    queryKey: ["doctor-documents"],
    queryFn: listDoctorDocuments,
  });

  // ── Local form state ─────────────────────────────────────────────────

  const [tab, setTab] = useState(0);

  const [basic, setBasic] = useState<BasicFormState>({
    fullName: "",
    qualification: "",
    qualificationCustom: "",
    experienceYears: "",
    consultationFee: "",
    bio: "",
    specializationNames: [],
  });

  const [verify, setVerify] = useState<VerificationFormState>({
    registrationNumber: "",
    registrationCouncil: "",
    registrationYear: "",
  });

  const [otherDocs, setOtherDocs] = useState<OtherDocUpload[]>([
    { file: null, notes: "" },
  ]);

  useEffect(() => {
    if (!data) return;
    const qual = data.qualification ?? "";
    const isPreset = QUALIFICATION_OPTIONS.some(
      (o) => o !== "Other" && o === qual,
    );
    setBasic({
      fullName: data.fullName ?? "",
      qualification: isPreset ? qual : qual ? "Other" : "",
      qualificationCustom: isPreset ? "" : qual,
      experienceYears: data.experienceYears?.toString() ?? "",
      consultationFee: data.consultationFee?.toString() ?? "",
      bio: data.bio ?? "",
      specializationNames: (data as DoctorDetail & { specializations?: string[] }).specializations ?? [],
    });
    setVerify({
      registrationNumber: data.registrationNumber ?? "",
      registrationCouncil: data.registrationCouncil ?? "",
      registrationYear: data.registrationYear?.toString() ?? "",
    });
  }, [data]);

  const resolvedQualification =
    basic.qualification === "Other"
      ? basic.qualificationCustom
      : basic.qualification;

  const basicComplete = Boolean(
    data?.fullName &&
      data?.qualification &&
      data?.consultationFee &&
      data.consultationFee > 0,
  );

  const status: DoctorProfileStatus = data?.profileStatus ?? "INCOMPLETE";

  const verifyLocked = status === "PENDING_REVIEW" || status === "APPROVED";

  // ── Derived document state ───────────────────────────────────────────

  const docs = existingDocs ?? [];
  const licenseDocs = docs.filter((d) => d.documentType === "LICENSE");
  const idProofDocs = docs.filter((d) => d.documentType === "ID_PROOF");
  const degreeDocs = docs.filter((d) => d.documentType === "DEGREE");
  const otherUploadedDocs = docs.filter((d) => d.documentType === "OTHER");
  const hasLicenseDoc = licenseDocs.length > 0;

  // ── Mutations ────────────────────────────────────────────────────────

  const saveBasic = useMutation({
    mutationFn: () =>
      updateDoctorProfile({
        fullName: basic.fullName,
        qualification: resolvedQualification,
        experienceYears: basic.experienceYears
          ? Number(basic.experienceYears)
          : undefined,
        consultationFee: basic.consultationFee
          ? Number(basic.consultationFee)
          : undefined,
        bio: basic.bio,
        specializationNames: basic.specializationNames,
      } as Partial<DoctorDetail> & { specializationNames?: string[] }),
    onSuccess: (d) => {
      qc.setQueryData(["doctor-profile"], d);
      setTab(1);
    },
  });

  const submitReview = useMutation({
    mutationFn: () => {
      const body: SubmitDoctorVerificationBody = {
        registrationNumber: verify.registrationNumber.trim(),
        registrationCouncil: verify.registrationCouncil.trim(),
        registrationYear: verify.registrationYear
          ? Number(verify.registrationYear)
          : undefined,
      };
      return submitDoctorForReview(body);
    },
    onSuccess: (d) => {
      qc.setQueryData(["doctor-profile"], d);
    },
  });

  const uploadMut = useMutation({
    mutationFn: ({
      file,
      type,
      notes,
    }: {
      file: File;
      type: DoctorDocumentType;
      notes?: string;
    }) => uploadDoctorDocument(file, type, notes),
    onSuccess: () => {
      refetchDocs();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteDoctorDocument(id),
    onSuccess: () => {
      refetchDocs();
    },
  });

  // ── File input refs ──────────────────────────────────────────────────

  const licenseInputRef = useRef<HTMLInputElement>(null);
  const idProofInputRef = useRef<HTMLInputElement>(null);
  const degreeInputRef = useRef<HTMLInputElement>(null);
  const otherInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleFileUpload = (
    type: DoctorDocumentType,
    file: File,
    notes?: string,
  ) => {
    uploadMut.mutate({ file, type, notes });
  };

  const handleDownload = async (doc: DoctorDocumentDto) => {
    const blob = await downloadDoctorDocument(doc.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Auto-advance to tab 1 when basic profile is already complete ─────

  useEffect(() => {
    if (basicComplete && tab === 0) {
      setTab(1);
    }
  }, [basicComplete]);

  if (isLoading) {
    return <Typography>Loading…</Typography>;
  }

  const specOptions: Specialization[] = specList ?? [];

  return (
    <Box>
      <PageHeader
        title="Become a verified doctor"
        subtitle="Complete the two steps below so our admin team can verify your credentials as per Indian medical rules."
      />

      {status === "APPROVED" && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/doctor/dashboard")}
            >
              Go to dashboard
            </Button>
          }
        >
          You're verified! Patients can now discover and book you.
        </Alert>
      )}
      {status === "PENDING_REVIEW" && (
        <Alert severity="info" icon={<HourglassTopIcon />} sx={{ mb: 3 }}>
          Your submission is under review. We'll email you within 2 business
          days.
        </Alert>
      )}
      {status === "REJECTED" && data?.rejectionReason && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Admin feedback:</strong> {data.rejectionReason}. Please update
          the fields below and resubmit.
        </Alert>
      )}

      {/* ── Tab header ───────────────────────────────────────────────── */}

      <Paper variant="outlined" sx={{ mb: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": { fontWeight: 600, py: 2 },
          }}
        >
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Step 1 — Basic Profile</span>
                {basicComplete && (
                  <CheckCircleIcon color="success" fontSize="small" />
                )}
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Step 2 — Verification & Documents</span>
                {(status === "PENDING_REVIEW" || status === "APPROVED") && (
                  <CheckCircleIcon color="success" fontSize="small" />
                )}
              </Stack>
            }
          />
        </Tabs>
      </Paper>

      {/* ── Tab 1: Basic profile ─────────────────────────────────────── */}

      <TabPanel value={tab} index={0}>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full name"
                value={basic.fullName}
                onChange={(e) =>
                  setBasic((s) => ({ ...s, fullName: e.target.value }))
                }
                required
              />
            </Grid>

            {/* ── Qualification dropdown with "Other" ──────────────── */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Qualification"
                value={basic.qualification}
                onChange={(e) =>
                  setBasic((s) => ({
                    ...s,
                    qualification: e.target.value,
                    qualificationCustom:
                      e.target.value === "Other" ? s.qualificationCustom : "",
                  }))
                }
                required
              >
                {QUALIFICATION_OPTIONS.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {basic.qualification === "Other" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Enter your qualification"
                  value={basic.qualificationCustom}
                  onChange={(e) =>
                    setBasic((s) => ({
                      ...s,
                      qualificationCustom: e.target.value,
                    }))
                  }
                  required
                  placeholder="e.g. MBBS, DNB (Cardiology)"
                />
              </Grid>
            )}

            {/* ── Specialization multi-select ──────────────────────── */}
            <Grid item xs={12} sm={basic.qualification === "Other" ? 6 : 12}>
              <Autocomplete
                multiple
                options={specOptions.map((s) => s.name)}
                value={basic.specializationNames}
                onChange={(_, v) =>
                  setBasic((s) => ({ ...s, specializationNames: v }))
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...rest } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option}
                        size="small"
                        {...rest}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Specializations"
                    placeholder="Select specializations"
                    helperText="Same list used in the patient search directory"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Years of experience"
                value={basic.experienceYears}
                onChange={(e) =>
                  setBasic((s) => ({ ...s, experienceYears: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Consultation fee (₹)"
                value={basic.consultationFee}
                onChange={(e) =>
                  setBasic((s) => ({ ...s, consultationFee: e.target.value }))
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Short bio"
                value={basic.bio}
                onChange={(e) =>
                  setBasic((s) => ({ ...s, bio: e.target.value }))
                }
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              variant="contained"
              disabled={
                saveBasic.isPending ||
                !basic.fullName ||
                !resolvedQualification ||
                !basic.consultationFee
              }
              onClick={() => saveBasic.mutate()}
            >
              {saveBasic.isPending ? "Saving…" : "Save & continue"}
            </Button>
          </Stack>
          {saveBasic.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(saveBasic.error as Error)?.message ?? "Failed to save profile."}
            </Alert>
          )}
        </Paper>
      </TabPanel>

      {/* ── Tab 2: Verification & documents ──────────────────────────── */}

      <TabPanel value={tab} index={1}>
        {!basicComplete && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please complete Step 1 first before filling verification details.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Registration details
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Provide your medical council registration. Our admin team reviews
            these before you become visible to patients.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration number"
                value={verify.registrationNumber}
                onChange={(e) =>
                  setVerify((v) => ({
                    ...v,
                    registrationNumber: e.target.value,
                  }))
                }
                required
                disabled={verifyLocked}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Medical council (e.g. MCI, Karnataka Medical Council)"
                value={verify.registrationCouncil}
                onChange={(e) =>
                  setVerify((v) => ({
                    ...v,
                    registrationCouncil: e.target.value,
                  }))
                }
                required
                disabled={verifyLocked}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Year of registration"
                value={verify.registrationYear}
                onChange={(e) =>
                  setVerify((v) => ({
                    ...v,
                    registrationYear: e.target.value,
                  }))
                }
                disabled={verifyLocked}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Document uploads ─────────────────────────────────────── */}

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Upload documents
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Upload your license, ID proof, and degree certificates. Files are
            securely stored. Accepted formats: PDF, JPEG, PNG (max 10 MB each).
          </Typography>

          {uploadMut.isPending && (
            <LinearProgress sx={{ mb: 2 }} />
          )}

          {/* License */}
          <DocumentUploadRow
            label="License / Registration Certificate"
            required
            docs={licenseDocs}
            locked={verifyLocked}
            onUpload={(file) => handleFileUpload("LICENSE", file)}
            onDownload={handleDownload}
            onDelete={(id) => deleteMut.mutate(id)}
            inputRef={licenseInputRef}
          />

          {/* ID Proof */}
          <DocumentUploadRow
            label="ID Proof (Aadhaar / PAN)"
            docs={idProofDocs}
            locked={verifyLocked}
            onUpload={(file) => handleFileUpload("ID_PROOF", file)}
            onDownload={handleDownload}
            onDelete={(id) => deleteMut.mutate(id)}
            inputRef={idProofInputRef}
          />

          {/* Degree */}
          <DocumentUploadRow
            label="Medical Degree Certificate"
            docs={degreeDocs}
            locked={verifyLocked}
            onUpload={(file) => handleFileUpload("DEGREE", file)}
            onDownload={handleDownload}
            onDelete={(id) => deleteMut.mutate(id)}
            inputRef={degreeInputRef}
          />
        </Paper>

        {/* ── Other documents ──────────────────────────────────────── */}

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Additional documents{" "}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
            >
              (optional, up to 5)
            </Typography>
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Upload any additional supporting documents with notes (e.g.
            experience certificates, specialization diplomas).
          </Typography>

          {/* Already uploaded "OTHER" docs */}
          {otherUploadedDocs.map((doc) => (
            <Stack
              key={doc.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                p: 1.5,
                mb: 1,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <DescriptionIcon color="action" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {doc.fileName}
                </Typography>
                {doc.notes && (
                  <Typography variant="caption" color="text.secondary">
                    {doc.notes}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatSize(doc.fileSize)}
              </Typography>
              <Tooltip title="Download">
                <IconButton size="small" onClick={() => handleDownload(doc)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!verifyLocked && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteMut.mutate(doc.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}

          {/* New "OTHER" upload rows */}
          {!verifyLocked &&
            otherUploadedDocs.length < 5 &&
            otherDocs.map((entry, idx) => (
              <Stack
                key={idx}
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "center" }}
                sx={{ mb: 1.5 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => otherInputRefs.current[idx]?.click()}
                  sx={{ minWidth: 160 }}
                >
                  {entry.file ? entry.file.name : "Choose file"}
                </Button>
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  ref={(el) => {
                    otherInputRefs.current[idx] = el;
                  }}
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setOtherDocs((prev) =>
                      prev.map((d, i) => (i === idx ? { ...d, file } : d)),
                    );
                  }}
                />
                <TextField
                  size="small"
                  placeholder="Notes (optional)"
                  value={entry.notes}
                  onChange={(e) =>
                    setOtherDocs((prev) =>
                      prev.map((d, i) =>
                        i === idx ? { ...d, notes: e.target.value } : d,
                      ),
                    )
                  }
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!entry.file || uploadMut.isPending}
                  onClick={() => {
                    if (!entry.file) return;
                    handleFileUpload("OTHER", entry.file, entry.notes || undefined);
                    setOtherDocs((prev) =>
                      prev.filter((_, i) => i !== idx),
                    );
                    if (otherDocs.length === 1) {
                      setOtherDocs([{ file: null, notes: "" }]);
                    }
                  }}
                >
                  Upload
                </Button>
                {otherDocs.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setOtherDocs((prev) => prev.filter((_, i) => i !== idx))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}

          {!verifyLocked &&
            otherUploadedDocs.length + otherDocs.length < 5 && (
              <Button
                size="small"
                onClick={() =>
                  setOtherDocs((prev) => [...prev, { file: null, notes: "" }])
                }
                sx={{ mt: 1 }}
              >
                + Add another document
              </Button>
            )}
        </Paper>

        {/* ── Submit for review ────────────────────────────────────── */}

        {submitReview.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(submitReview.error as Error)?.message ??
              "Submission failed. Please try again."}
          </Alert>
        )}
        {submitReview.isSuccess && status === "PENDING_REVIEW" && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mb: 2 }}
          >
            Submitted for review. You'll receive an email once an admin has
            verified your credentials.
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={() => setTab(0)}>
            Back to Step 1
          </Button>
          <Button
            variant="contained"
            disabled={
              !basicComplete ||
              submitReview.isPending ||
              !verify.registrationNumber ||
              !verify.registrationCouncil ||
              !hasLicenseDoc ||
              verifyLocked
            }
            onClick={() => submitReview.mutate()}
          >
            {submitReview.isPending
              ? "Submitting…"
              : status === "PENDING_REVIEW"
                ? "Awaiting review"
                : status === "APPROVED"
                  ? "Verified"
                  : status === "REJECTED"
                    ? "Resubmit for review"
                    : "Submit for review"}
          </Button>
        </Stack>
      </TabPanel>
    </Box>
  );
}

// ── Reusable document upload row ─────────────────────────────────────────

function DocumentUploadRow({
  label,
  required,
  docs,
  locked,
  onUpload,
  onDownload,
  onDelete,
  inputRef,
}: {
  label: string;
  required?: boolean;
  docs: DoctorDocumentDto[];
  locked: boolean;
  onUpload: (file: File) => void;
  onDownload: (doc: DoctorDocumentDto) => void;
  onDelete: (id: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const uploaded = docs[0];

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label}
        {required && (
          <Typography component="span" color="error.main">
            {" "}
            *
          </Typography>
        )}
      </Typography>

      {uploaded ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            p: 1.5,
            bgcolor: "success.50",
            border: 1,
            borderColor: "success.200",
            borderRadius: 1,
          }}
        >
          <DescriptionIcon color="success" />
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>
            {uploaded.fileName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatSize(uploaded.fileSize)}
          </Typography>
          <Tooltip title="Download">
            <IconButton size="small" onClick={() => onDownload(uploaded)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!locked && (
            <>
              <Tooltip title="Replace">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => inputRef.current?.click()}
                >
                  Replace
                </Button>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(uploaded.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      ) : (
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={locked}
        >
          Upload file
        </Button>
      )}

      <input
        type="file"
        hidden
        accept=".pdf,.jpg,.jpeg,.png"
        ref={inputRef as React.RefObject<HTMLInputElement>}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </Box>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}
